import { AssigneeSelectComponent, AssigneeChange } from '../../company/assignee-select/assignee-select';
import { CompanyContextService } from '../../../services/company-context';
import { Component, Input, OnInit, OnDestroy, DoCheck, ChangeDetectorRef, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DealService, CargoDealDetail, CreateDealActivityRequest, DealActivity, CompleteDealActivityRequest } from '../../../services/api/deal';
import { FileService, FileDto } from '../../../services/api/file';
import { RequisitesService, CompanyRequisites } from '../../../services/api/requisites';
import { DictionaryService, DealActivityTypeDto, RoleDto } from '../../../services/api/dictionary';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { Subscription, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ViewRequisitesComponent } from '../view-requisites/view-requisites.component';
import { ChatComponent } from '../chat/chat.component';
import { ConfirmDialogService } from '../../../services/confirm-dialog';
import { DocNumberPipe } from '../../../services/request-number';
import { DELIVERED_CODES, visibleActivities } from '../../../services/deal-stage';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { INVOICE_ACTIVITY_CODE, PAYMENT_ACTIVITY_CODE, formatInvoiceMessage, formatInvoiceEditMessage, formatInvoiceRevokeMessage, formatPaymentMessage,
  formatPaymentEditMessage, formatPaymentRevokeMessage, formatPaymentRevokeRequestMessage, isEditable, REVOKE_APPROVED, REVOKE_DECLINED, paymentSummary, DealInvoice, DealPayment, CurrencyTotals, INVOICE_CURRENCIES } from '../../../services/deal-payments';
import { ChatService, ChatMessage } from '../../../services/api/chat';
import { ChatSocketService } from '../../../services/api/chat-socket';
import { lockPageScroll, unlockPageScroll } from '../../../services/scroll-lock';

/** Последнее прочитанное сообщение чата сделки — в браузере, пока на сервере нет счётчика непрочитанных. */
const DEAL_CHAT_SEEN_KEY = 'bilload.chat.lastSeen.deal';

/** «Документы → Другие документы»: сюда же попадает файл счёта. */
const OTHER_DOCS_CODE = 'DOC_OTHER';

@Component({
  selector: 'app-view-deal',
  standalone: true,
  imports: [CommonModule, RouterModule, AssigneeSelectComponent, ReactiveFormsModule, FormsModule, ViewRequisitesComponent, ChatComponent, DocNumberPipe],
  templateUrl: './view-deal.html',
  styleUrl: './view-deal.scss'
})
export class ViewDealComponent implements OnInit, OnDestroy, DoCheck {
  /** Подтверждение на странице вместо системного confirm() — его блокируют встроенные браузеры. */
  private confirmDialog = inject(ConfirmDialogService);
  private chatService = inject(ChatService);
  private chatSocket = inject(ChatSocketService);

  /** Встроенный режим (вкладка «Перевозка» на странице обработки расчёта) — id сделки
   *  приходит напрямую, а не из параметров маршрута. */
  @Input() embeddedDealId?: number;
  /** Только блок «Документы» со всеми папками, которые отдаёт сервер (вкладка «Таможенная очистка» поставки). */
  @Input() documentsOnly = false;

  deal: CargoDealDetail | null = null;
  loading = false;
  error: string | null = null;
  isCargoOwner = false;
  isFreightForwarder = false;
  isCustomsBroker = false;

  // Модальное окно для просмотра запроса
  showRequestModal = false;

  // Модальное окно для просмотра ставки
  showBidModal = false;

  // Модальное окно для просмотра реквизитов
  showRequisitesModal = false;
  cargoOwnerRequisites: CompanyRequisites | null = null;
  loadingRequisites = false;
  requisitesError: string | null = null;
  requisitesCompanyName: string = '';

  // Модальное окно для просмотра активности
  showActivityModal = false;
  selectedActivity: DealActivity | null = null;

  // Создание активности (запрос)
  showAddActivityModal = false;
  activityForm: FormGroup;
  creatingActivity = false;
  activityError: string | null = null;

  // Загрузка документа и создание активности
  showDocumentUploadModal = false;
  selectedDocumentType: DealActivityTypeDto | null = null;
  documentUploadFiles: File[] = [];
  documentActorMessage = '';
  documentUploadError: string | null = null;
  creatingDocumentActivity = false;
  documentModalShowingUploadForm = false;
  deletingFileUid: string | null = null;

  // Изменение статуса
  showAddStatusModal = false;
  statusForm: FormGroup;
  creatingStatus = false;
  statusError: string | null = null;
  
  // Завершение активности (для запросов)
  showCompleteActivityModal = false;
  completeActivityForm: FormGroup;
  completingActivity = false;
  completeActivityError: string | null = null;
  
  // Форма завершения активности прямо в модальном окне просмотра
  completeActivityInlineForm: FormGroup;

  // Справочники
  activityTypes: DealActivityTypeDto[] = [];
  roles: RoleDto[] = [];
  loadingActivityTypes = false;
  
  // Загрузка файлов к активности
  activityFilesMap: Map<number, FileDto[]> = new Map();
  loadingFilesMap: Map<number, boolean> = new Map();
  uploadingFilesMap: Map<number, boolean> = new Map();
  selectedFilesMap: Map<number, File[]> = new Map();
  showFileUploadMap: Map<number, boolean> = new Map();
  
  // Файлы для завершения активности
  selectedFilesForComplete: File[] = [];

  // Translations
  titleText = 'Сделка';
  loadingText = 'Загрузка...';
  dealInfoText = 'Информация о сделке';
  dealNumberText = 'Номер сделки';
  statusText = 'Статус';
  createdAtText = 'Дата создания';
  cargoOwnerText = 'Грузовладелец';
  freightForwarderText = 'Экспедитор';
  companyText = 'Компания';
  contactPersonText = 'Контактное лицо';
  emailText = 'Email';
  phoneText = 'Телефон';
  cargoRequestText = 'Запрос на перевозку';
  bidInfoText = 'Информация о ставке';
  priceText = 'Цена';
  deliveryDaysText = 'Срок доставки';
  daysText = 'дней';
  servicesText = 'Услуги';
  noteText = 'Примечание';
  activitiesText = 'Активности';
  noActivitiesText = 'Активности пока не добавлены';
  statusBlockText = 'Статус';
  noStatusText = 'Нет данных о статусе';
  documentsText = 'Документы';
  chatButtonText = 'Чат';
  requestsText = 'Запросы';
  noRequestsText = 'Запросов пока нет';
  createActivityText = 'Создать активность';
  saveText = 'Сохранить';
  savingText = 'Сохранение...';
  uploadText = 'Загрузить';
  noFilesText = 'Файлы не загружены';
  activityDealCreatedText = 'Сделка создана';
  activityLoadingDateSetText = 'Дата погрузки назначена';
  activityDistanceUpdatedText = 'Обновлено расстояние';
  activityInTransitText = 'Груз в пути';
  activityArrivedAtDestinationText = 'Груз прибыл в точку назначения';
  activityDeliveredText = 'Груз доставлен';
  activityCustomsPassedText = 'Таможенная очистка пройдена';
  activityDelayReportedText = 'Задержка в доставке';
  activityIssueReportedText = 'Зафиксирована проблема';
  activityCargoAcceptedText = 'Груз принят экспедитором';
  activityAtReceiverWarehouseText = 'Груз на складе получателя';
  activityReceivedByReceiverText = 'Получатель подтвердил получение';
  activityDocumentSignedByForwarderText = 'Документ подписан экспедитором';
  activityDocumentSignedByCargoOwnerText = 'Документ подписан грузовладельцем';
  backText = 'Назад';
  viewRequestText = 'Просмотр запроса';
  viewBidText = 'Просмотр ставки';
  addActivityText = 'Добавить активность';
  createRequestText = 'Запросить';
  activityTypeText = 'Тип активности';
  loadingDateLabel = 'Дата погрузки';
  loadingTimeLabel = 'Время погрузки';
  distanceLabel = 'Расстояние (км)';
  locationLabel = 'Текущее местоположение';
  descriptionLabel = 'Описание';
  submitText = 'Добавить';
  cancelText = 'Отмена';
  creatingText = 'Добавление...';
  filesLabel = 'Прикрепить файлы';
  uploadingFilesText = 'Загрузка файлов...';
  chooseFilesText = 'Выбрать файлы';
  showRequisitesText = 'Показать реквизиты';
  requisitesText = 'Реквизиты';
  messageLabel = 'Сообщение';
  isRequestLabel = 'Это запрос на действие';
  targetRoleLabel = 'Целевая роль';
  completeActivityText = 'Завершить активность';
  completeActivityMessageLabel = 'Ответное сообщение';
  completingText = 'Завершение...';
  requestPendingText = 'Ожидает подтверждения';
  requestCompletedText = 'Подтверждено';
  activityCompletedSuccessText = 'Активность успешно завершена';
  changeStatusText = 'Изменить статус';
  documentCommentLabel = 'Описание файла (необязательно)';
  initiatorText = 'Инициатор';
  requestRecipientText = 'Получатель запроса';
  requestStatusText = 'Статус запроса';
  actorMessageText = 'Сообщение от инициатора';
  targetMessageText = 'Ответное сообщение';
  documentLabel = 'Документ';
  deleteFileText = 'Удалить файл';
  statusLabel = 'Статус';
  requestLabel = 'Запрос';
  reportBlockTitleText = 'Отчёт по сделке';
  generateReportText = 'Сформировать отчёт';
  regenerateReportText = 'Перегенерировать';
  reportGeneralInfoText = 'Общая информация';
  reportTimelineText = 'Хронология';
  reportProsText = 'Плюсы';
  reportConsText = 'Минусы';
  reportRatingText = 'Оценка';
  reportConclusionText = 'Итог';

  routeLabelText = 'Маршрут:';
  productsCountLabelText = 'Товаров:';
  openReportSeparatePageText = 'Для просмотра отчёта откройте отдельную страницу.';
  openReportText = 'Открыть отчёт';
  selectStatusText = 'Выберите статус';
  cityPlaceholderExampleText = 'Напр. г. Милан';
  activityMessagePlaceholderText = 'Введите сообщение или описание активности';
  selectRoleText = 'Выберите роль';
  targetMessagePlaceholderText = 'Введите ответное сообщение';
  requestColonLabelText = 'Запрос:';

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dealService: DealService,
    private fileService: FileService,
    private requisitesService: RequisitesService,
    private dictionaryService: DictionaryService,
    private languageService: LanguageService,
    private authService: AuthService,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    public companyContext: CompanyContextService,
    private cdr: ChangeDetectorRef
  ) {
    this.activityForm = this.fb.group({
      activityTypeCode: ['REQUEST'],
      actorMessage: ['', Validators.required],
      request: [true],
      targetRoleId: [null, Validators.required]
    });
    
    this.completeActivityForm = this.fb.group({
      targetMessage: ['']
    });
    
    this.completeActivityInlineForm = this.fb.group({
      targetMessage: ['']
    });

    this.statusForm = this.fb.group({
      activityTypeCode: ['', Validators.required],
      actorMessage: ['']
    });
  }

  ngOnInit(): void {
    this.companyContext.ensureLoaded();
    const user = this.authService.getUser();
    this.isCargoOwner = user?.role?.code === 'ROLE_CARGO_OWNER';
    this.isFreightForwarder = user?.role?.code === 'ROLE_FREIGHT_FORWARDER';
    this.isCustomsBroker = user?.role?.code === 'ROLE_CUSTOMS_BROKER';

    this.updateTranslations();
    this.loadActivityTypes();
    this.loadRoles();

    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );

    if (this.embeddedDealId) {
      this.loadDeal(this.embeddedDealId);
      return;
    }

    this.subscriptions.add(
      this.route.params.subscribe(params => {
        const dealId = +params['id'];
        if (dealId) {
          this.loadDeal(dealId);
        }
      })
    );
  }
  
  private loadActivityTypes(): void {
    this.loadingActivityTypes = true;
    this.subscriptions.add(
      this.dictionaryService.getAllDealActivityTypes().subscribe({
        next: (types) => {
          this.activityTypes = types;
          this.loadingActivityTypes = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading activity types:', err);
          this.loadingActivityTypes = false;
          this.cdr.markForCheck();
        }
      })
    );
  }
  
  private loadRoles(): void {
    this.subscriptions.add(
      this.dictionaryService.getRoles().subscribe({
        next: (roles) => {
          this.roles = roles;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading roles:', err);
        }
      })
    );
  }

  ngOnDestroy(): void {
    clearInterval(this.nowTimer);
    this.closePreview();
    if (this.chatCloseTimer) clearTimeout(this.chatCloseTimer);
    if (this.chatOpen || this.modalScrollLocked) this.unlockScroll();
    this.subscriptions.unsubscribe();
  }

  // ─── Отображение (общий стиль с кабинетом экспедитора) ───────────────────

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  private get numberLocale(): string {
    const lang = this.languageService.getCurrentLanguageValue();
    return ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
  }

  rateLabel(rateType: string | null | undefined): string {
    switch (rateType) {
      case 'FTL': return this.t('ffSearch.rateFtl', 'Фура (FTL)');
      case 'LTL': return this.t('ffSearch.rateLtl', 'Сборный (LTL)');
      case 'AIR': return this.t('ffSearch.rateAir', 'Авиа');
      case 'CONTAINER': return this.t('ffSearch.rateContainer', 'Контейнер');
      default: return rateType || '';
    }
  }

  private productVolume(p: any): number {
    if (p.volumeM3) return p.volumeM3;
    if (p.lengthMm && p.widthMm && p.heightMm) return (p.lengthMm * p.widthMm * p.heightMm) / 1e9 * (p.quantity || 1);
    return 0;
  }

  get totalWeight(): number {
    return (this.deal?.cargoRequest?.products ?? []).reduce((sum: number, p: any) => sum + (p.totalWeightKg || 0), 0);
  }

  get totalVolume(): number {
    return (this.deal?.cargoRequest?.products ?? []).reduce((sum: number, p: any) => sum + this.productVolume(p), 0);
  }

  get placesCount(): number {
    return (this.deal?.cargoRequest?.products ?? []).reduce((sum: number, p: any) => sum + (p.quantity || 1), 0);
  }

  fmtWeight(kg: number): string {
    if (!kg) return '—';
    return `${new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 1 }).format(kg)} ${this.t('ffSearch.kg', 'кг')}`;
  }

  fmtVolume(m3: number): string {
    if (!m3) return '—';
    return new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: m3 < 1 ? 3 : 1 }).format(m3) + ' м³';
  }

  fmtMoney(amount: number | undefined, code: string | undefined): string {
    return `${new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 2 }).format(amount || 0)} ${code || ''}`.trim();
  }

  // ─── Окно «Запрос» — как основная страница запроса ───

  productQty(p: any): number {
    return p?.quantity || 1;
  }

  productVolumeOf(p: any): number {
    return this.productVolume(p);
  }

  productTypeName(p: any): string {
    const ct = p?.cargoType;
    if (!ct) return '';
    return this.languageService.getCurrentLanguageValue() === 'en' ? (ct.nameEn || ct.nameRu) : (ct.nameRu || ct.nameEn);
  }

  /** Особенности груза: флаг хотя бы у одной позиции — показываем его, иначе — обратное. */
  get cargoTraits(): { label: string; warn: boolean }[] {
    const products: any[] = this.deal?.cargoRequest?.products ?? [];
    const any = (key: string) => products.some(p => !!p[key]);
    return [
      any('isDangerous') ? { label: this.t('ffSearch.dangerous', 'Опасный груз'), warn: true } : { label: this.t('ffRequest.notDangerous', 'Неопасный груз'), warn: false },
      any('isOversized') ? { label: this.t('ffSearch.oversized', 'Крупногабаритный груз'), warn: true } : { label: this.t('ffRequest.notOversized', 'Стандартные габариты'), warn: false },
      any('isNonStackable') ? { label: this.t('ffSearch.nonStackable', 'Не штабелировать'), warn: false } : { label: this.t('ffRequest.stackable', 'Можно штабелировать'), warn: false },
    ];
  }

  serviceNames(bid: any): string {
    return (bid?.services ?? []).map((s: any) => this.getServiceName(s)).join(', ');
  }

  // ─── Счётчик дней: сделка создана → груз передан на перевозку → груз доставлен ───

  /** Этапы, после которых груз считается переданным экспедитору (берём самый ранний). */
  private static readonly HANDOVER_CODES = ['CARGO_PICKUP_DONE', 'CARGO_AT_FORWARDER_WAREHOUSE', 'IN_TRANSIT'];

  private firstActivityAt(codes: string[]): Date | null {
    const dates = (this.deal?.activities ?? [])
      .filter(a => codes.includes(a.activityType?.code))
      .map(a => new Date(a.createdAt))
      .filter(d => !isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    return dates[0] ?? null;
  }

  /** Календарные дни между датами (без учёта времени). */
  private daysBetween(from: Date, to: Date): number {
    const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
    const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
    return Math.max(0, Math.round((b - a) / 86400000));
  }

  get dealTimeline(): {
    created: Date | null; handover: Date | null; delivered: Date | null;
    toHandover: number | null; inTransit: number | null; total: number | null;
    planDays: number | null; overdue: boolean;
  } {
    const created = this.deal?.createdAt ? new Date(this.deal.createdAt) : null;
    const handover = this.firstActivityAt(ViewDealComponent.HANDOVER_CODES);
    const delivered = this.firstActivityAt(DELIVERED_CODES);
    const now = new Date();
    const toHandover = created ? this.daysBetween(created, handover ?? delivered ?? now) : null;
    const transitStart = handover ?? null;
    const inTransit = transitStart ? this.daysBetween(transitStart, delivered ?? now) : null;
    const total = created ? this.daysBetween(created, delivered ?? now) : null;
    const planDays = this.deal?.bid?.deliveryDays ?? null;
    const overdue = planDays != null && inTransit != null && inTransit > planDays;
    return { created, handover, delivered, toHandover, inTransit, total, planDays, overdue };
  }

  daysLabel(n: number): string {
    const lang = this.languageService.getCurrentLanguageValue();
    if (lang === 'en') return `${n} ${n === 1 ? 'day' : 'days'}`;
    if (lang !== 'ru') return `${n} ${this.t('ffDeal.daysShort', 'дн.')}`;
    const m10 = n % 10, m100 = n % 100;
    const word = m10 === 1 && m100 !== 11 ? 'день' : m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20) ? 'дня' : 'дней';
    return `${n} ${word}`;
  }

  /** Сколько типов документов уже загружено — для счётчика «3 / 8». */
  get docsReadyCount(): number {
    return this.documentTypes.filter(d => this.hasDocumentsForType(d)).length;
  }

  // ─── Чат — окно-мессенджер из кнопки (как на странице запроса) ───────────

  chatOpen = false;
  chatClosing = false;
  chatBox: { top: number; left: number; width: number; height: number; origin: string } | null = null;
  private chatAnchor: HTMLElement | null = null;
  private chatCloseTimer: ReturnType<typeof setTimeout> | null = null;

  /** Переход из уведомления (?chat=1) — сразу открываем окно чата и убираем параметр из адреса. */
  private chatLinkWatched = false;

  private openChatFromLink(): void {
    if (!this.chatLinkWatched) {
      this.chatLinkWatched = true;
      this.subscriptions.add(this.route.queryParamMap.subscribe(() => this.openChatFromLink()));
      return;
    }
    if (this.route.snapshot.queryParamMap.get('chat') !== '1' || !this.deal) return;
    const btn = document.querySelector('.deal-chat-fab') as HTMLElement | null;
    if (!this.chatOpen) this.openChat(btn ?? undefined);
    this.router.navigate([], { relativeTo: this.route, queryParams: { chat: null }, queryParamsHandling: 'merge', replaceUrl: true });
    this.cdr.markForCheck();
  }

  // ─── Непрочитанные сообщения чата сделки (число на кнопке) ───
  unreadCount = 0;
  private watchedDealId: number | null = null;
  private lastSeenId = 0;
  private latestId = 0;
  private unreadIds = new Set<number>();

  private watchDealChat(dealId: number): void {
    if (this.watchedDealId === dealId) return;
    this.watchedDealId = dealId;
    const me = this.authService.getUser()?.id;
    this.lastSeenId = this.readSeen(dealId);
    const track = (m: ChatMessage) => {
      if (m.id > this.latestId) this.latestId = m.id;
      if (this.chatOpen) { this.markSeen(); return; }
      if (m.sender?.id !== me && !m.deleted && m.id > this.lastSeenId) this.unreadIds.add(m.id);
      this.unreadCount = this.unreadIds.size;
    };
    this.subscriptions.add(
      this.chatService.getMessages('DEAL', dealId, 0, 50).subscribe({
        next: res => { res.content.forEach(track); this.cdr.markForCheck(); },
        error: () => {}
      })
    );
    this.subscriptions.add(
      this.chatSocket.watch('DEAL', dealId).subscribe({
        next: ev => {
          if (ev.type === 'CREATED') track(ev.message);
          else if (ev.type === 'DELETED') { this.unreadIds.delete(ev.message.id); this.unreadCount = this.unreadIds.size; }
          this.cdr.markForCheck();
        },
        error: () => {}
      })
    );
  }

  private readSeen(dealId: number): number {
    try { return JSON.parse(localStorage.getItem(DEAL_CHAT_SEEN_KEY) || '{}')[dealId] || 0; } catch { return 0; }
  }

  private markSeen(): void {
    this.unreadIds.clear();
    this.unreadCount = 0;
    const id = this.watchedDealId;
    if (!id || this.latestId <= this.lastSeenId) return;
    this.lastSeenId = this.latestId;
    try {
      const map = JSON.parse(localStorage.getItem(DEAL_CHAT_SEEN_KEY) || '{}');
      map[id] = this.latestId;
      localStorage.setItem(DEAL_CHAT_SEEN_KEY, JSON.stringify(map));
    } catch { /* приватный режим — счётчик просто не сохранится */ }
  }

  openChat(anchor?: HTMLElement): void {
    this.markSeen();
    if (this.chatCloseTimer) { clearTimeout(this.chatCloseTimer); this.chatCloseTimer = null; }
    if (anchor) this.chatAnchor = anchor;
    this.chatBox = this.measureChatBox();
    this.chatClosing = false;
    this.chatOpen = true;
    this.lockScroll();
  }

  closeChat(): void {
    if (!this.chatOpen || this.chatClosing) return;
    this.markSeen();
    this.chatBox = this.measureChatBox() ?? this.chatBox;
    this.chatClosing = true;
    this.chatCloseTimer = setTimeout(() => {
      this.chatOpen = false;
      this.chatClosing = false;
      this.chatCloseTimer = null;
      this.unlockScroll();
      this.cdr.markForCheck();
    }, 200);
  }

  /** Старая кнопка «Чат» вела вниз страницы — теперь открывает окно. */
  scrollToChat(): void {
    this.openChat();
  }

  private lockScroll(): void {
    lockPageScroll();
  }

  private unlockScroll(): void {
    unlockPageScroll();
  }

  private measureChatBox(): { top: number; left: number; width: number; height: number; origin: string } | null {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rect = this.chatAnchor?.getBoundingClientRect();
    if (vw < 640) {
      const ox = rect ? rect.left + rect.width / 2 : vw / 2;
      const oy = rect ? rect.top + rect.height / 2 : vh / 2;
      return { top: 0, left: 0, width: vw, height: vh, origin: `${ox}px ${oy}px` };
    }
    const width = Math.min(420, vw - 32);
    const height = Math.min(640, vh - 32);
    if (!rect) return { top: vh - 16 - height, left: vw - 16 - width, width, height, origin: '100% 100%' };
    const left = Math.max(16, Math.min(rect.right - width, vw - 16 - width));
    let top = rect.bottom + 10;
    // кнопка внизу экрана — окно открываем над ней
    if (top + height > vh - 16) top = rect.top - 10 - height >= 16 ? rect.top - 10 - height : Math.max(16, vh - 16 - height);
    return { top, left, width, height, origin: `${rect.left + rect.width / 2 - left}px ${rect.top + rect.height / 2 - top}px` };
  }

  @HostListener('window:resize')
  onChatResize(): void {
    if (this.chatOpen && !this.chatClosing) this.chatBox = this.measureChatBox();
  }

  @HostListener('document:keydown.escape')
  onChatEscape(): void {
    if (this.chatOpen) this.closeChat();
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('deal.title') || 'Сделка';
    this.loadingText = this.languageService.translate('deal.loading') || 'Загрузка...';
    this.dealInfoText = this.languageService.translate('deal.dealInfo') || 'Информация о сделке';
    this.dealNumberText = this.languageService.translate('deal.dealNumber') || 'Номер сделки';
    this.statusText = this.languageService.translate('deal.status') || 'Статус';
    this.createdAtText = this.languageService.translate('deal.createdAt') || 'Дата создания';
    this.cargoOwnerText = this.languageService.translate('deal.cargoOwner') || 'Грузовладелец';
    this.freightForwarderText = this.languageService.translate('deal.freightForwarder') || 'Экспедитор';
    this.companyText = this.languageService.translate('deal.company') || 'Компания';
    this.contactPersonText = this.languageService.translate('deal.contactPerson') || 'Контактное лицо';
    this.emailText = this.languageService.translate('deal.email') || 'Email';
    this.phoneText = this.languageService.translate('deal.phone') || 'Телефон';
    this.cargoRequestText = this.languageService.translate('deal.cargoRequest') || 'Запрос на перевозку';
    this.bidInfoText = this.languageService.translate('deal.bidInfo') || 'Информация о ставке';
    this.priceText = this.languageService.translate('deal.price') || 'Цена';
    this.deliveryDaysText = this.languageService.translate('deal.deliveryDays') || 'Срок доставки';
    this.daysText = this.languageService.translate('deal.days') || 'дней';
    this.servicesText = this.languageService.translate('deal.services') || 'Услуги';
    this.noteText = this.languageService.translate('deal.note') || 'Примечание';
    this.activitiesText = this.languageService.translate('deal.activities') || 'Активности';
    this.noActivitiesText = this.languageService.translate('deal.noActivities') || 'Активности пока не добавлены';
    this.statusBlockText = this.languageService.translate('deal.statusBlock') || 'Статус';
    this.noStatusText = this.languageService.translate('deal.noStatus') || 'Нет данных о статусе';
    this.documentsText = this.languageService.translate('deal.documents') || 'Документы';
    this.chatButtonText = this.languageService.translate('deal.chatButton') || 'Чат';
    this.requestsText = this.languageService.translate('deal.requests') || 'Запросы';
    this.noRequestsText = this.languageService.translate('deal.noRequests') || 'Запросов пока нет';
    this.createActivityText = this.languageService.translate('deal.createActivity') || 'Создать активность';
    this.saveText = this.languageService.translate('deal.save') || 'Сохранить';
    this.savingText = this.languageService.translate('deal.saving') || 'Сохранение...';
    this.uploadText = this.languageService.translate('deal.upload') || 'Загрузить';
    this.noFilesText = this.languageService.translate('deal.noFiles') || 'Файлы не загружены';
    this.backText = this.languageService.translate('deal.back') || 'Назад';
    this.viewRequestText = this.languageService.translate('deal.viewRequest') || 'Просмотр запроса';
    this.viewBidText = this.languageService.translate('deal.viewBid') || 'Просмотр ставки';
    this.addActivityText = this.languageService.translate('deal.addActivity') || 'Добавить активность';
    this.createRequestText = this.languageService.translate('deal.createRequest') || 'Создать запрос';
    this.activityTypeText = this.languageService.translate('deal.activityType') || 'Тип активности';
    this.loadingDateLabel = this.languageService.translate('deal.loadingDate') || 'Дата погрузки';
    this.loadingTimeLabel = this.languageService.translate('deal.loadingTime') || 'Время погрузки';
    this.distanceLabel = this.languageService.translate('deal.distance') || 'Расстояние (км)';
    this.locationLabel = this.languageService.translate('deal.location') || 'Текущее местоположение';
    this.descriptionLabel = this.languageService.translate('deal.description') || 'Описание';
    this.submitText = this.languageService.translate('deal.submit') || 'Добавить';
    this.cancelText = this.languageService.translate('deal.cancel') || 'Отмена';
    this.creatingText = this.languageService.translate('deal.creating') || 'Добавление...';
    this.filesLabel = this.languageService.translate('deal.files') || 'Прикрепить файлы';
    this.uploadingFilesText = this.languageService.translate('deal.uploadingFiles') || 'Загрузка файлов...';
    this.chooseFilesText = this.languageService.translate('deal.chooseFiles') || 'Выбрать файлы';
    this.showRequisitesText = this.languageService.translate('deal.showRequisites') || 'Показать реквизиты';
    this.requisitesText = this.languageService.translate('ffDeal.requisitesTitle') || 'Реквизиты';
    this.messageLabel = this.languageService.translate('deal.message') || 'Сообщение';
    this.targetRoleLabel = this.languageService.translate('deal.targetRole') || 'Получатель запроса';
    this.changeStatusText = this.languageService.translate('deal.changeStatus') || 'Изменить статус';
    this.documentCommentLabel = this.languageService.translate('deal.documentComment') || 'Описание файла (необязательно)';
    this.initiatorText = this.languageService.translate('deal.initiator') || 'Инициатор';
    this.requestRecipientText = this.languageService.translate('deal.requestRecipient') || 'Получатель запроса';
    this.requestStatusText = this.languageService.translate('deal.requestStatus') || 'Статус запроса';
    this.actorMessageText = this.languageService.translate('deal.actorMessage') || 'Сообщение от инициатора';
    this.targetMessageText = this.languageService.translate('deal.targetMessage') || 'Ответное сообщение';
    this.documentLabel = this.languageService.translate('deal.document') || 'Документ';
    this.deleteFileText = this.languageService.translate('deal.deleteFile') || 'Удалить файл';
    this.statusLabel = this.languageService.translate('deal.statusActivity') || 'Статус';
    this.requestLabel = this.languageService.translate('deal.requestActivity') || 'Запрос';
    this.reportBlockTitleText = this.languageService.translate('deal.reportBlockTitle') || 'Отчёт по сделке';
    this.generateReportText = this.languageService.translate('deal.generateReport') || 'Сформировать отчёт';
    this.regenerateReportText = this.languageService.translate('deal.regenerateReport') || 'Перегенерировать';
    this.reportGeneralInfoText = this.languageService.translate('deal.reportGeneralInfo') || 'Общая информация';
    this.reportTimelineText = this.languageService.translate('deal.reportTimeline') || 'Хронология';
    this.reportProsText = this.languageService.translate('deal.reportPros') || 'Плюсы';
    this.reportConsText = this.languageService.translate('deal.reportCons') || 'Минусы';
    this.reportRatingText = this.languageService.translate('deal.reportRating') || 'Оценка';
    this.reportConclusionText = this.languageService.translate('deal.reportConclusion') || 'Итог';

    this.routeLabelText = this.languageService.translate('deal.routeLabel') || this.routeLabelText;
    this.productsCountLabelText = this.languageService.translate('deal.productsCountLabel') || this.productsCountLabelText;
    this.openReportSeparatePageText = this.languageService.translate('deal.openReportSeparatePage') || this.openReportSeparatePageText;
    this.openReportText = this.languageService.translate('deal.openReport') || this.openReportText;
    this.selectStatusText = this.languageService.translate('deal.selectStatus') || this.selectStatusText;
    this.cityPlaceholderExampleText = this.languageService.translate('deal.cityPlaceholderExample') || this.cityPlaceholderExampleText;
    this.activityMessagePlaceholderText = this.languageService.translate('deal.activityMessagePlaceholder') || this.activityMessagePlaceholderText;
    this.selectRoleText = this.languageService.translate('deal.selectRole') || this.selectRoleText;
    this.targetMessagePlaceholderText = this.languageService.translate('deal.targetMessagePlaceholder') || this.targetMessagePlaceholderText;
    this.requestColonLabelText = this.languageService.translate('deal.requestColonLabel') || this.requestColonLabelText;
  }

  private loadDeal(dealId: number): void {
    this.loading = true;
    this.error = null;

    this.subscriptions.add(
      this.dealService.getDealDetail(dealId).subscribe({
        next: (deal) => {
          // Экспедитор не видит статусы, которые ставил брокер
          this.deal = { ...deal, activities: visibleActivities(deal.activities, this.isFreightForwarder ? 'ROLE_FREIGHT_FORWARDER' : undefined) };
          this.watchDealChat(deal.id);
          setTimeout(() => this.openChatFromLink());
          this.loading = false;
          
          // Загрузить файлы всех активностей одним запросом
          if (deal.activities && deal.activities.length > 0) {
            this.loadAllActivityFiles(deal.activities.map(a => a.id));
          }
          
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading deal:', err);
          this.error = 'Ошибка при загрузке сделки';
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  goBack(): void {
    if (this.isCargoOwner) {
      this.router.navigate(['/cargo-owner/dashboard/deals']);
    } else if (this.isFreightForwarder) {
      this.router.navigate(['/freight-forwarder/dashboard/deals']);
    } else if (this.isCustomsBroker) {
      this.router.navigate(['/customs-broker/dashboard/deals']);
    } else {
      this.router.navigate(['/']);
    }
  }

  viewRequest(): void {
    if (this.deal?.cargoRequest?.id) {
      this.showRequestModal = true;
    }
  }

  closeRequestModal(): void {
    this.showRequestModal = false;
  }

  viewBid(): void {
    if (this.deal?.bid) {
      this.showBidModal = true;
    }
  }

  closeBidModal(): void {
    this.showBidModal = false;
  }

  viewRequisites(): void {
    if (!this.deal) return;
    
    this.showRequisitesModal = true;
    this.loadingRequisites = true;
    this.requisitesError = null;
    
    // Определяем ID пользователя, чьи реквизиты нужно показать
    // Если текущий пользователь - грузовладелец, показываем реквизиты экспедитора
    // Если текущий пользователь - экспедитор, показываем реквизиты грузовладельца
    const targetUserId = this.isCargoOwner 
      ? this.deal.freightForwarder.id 
      : this.deal.cargoOwner.id;
    
    // Сохраняем название компании для заголовка модального окна
    this.requisitesCompanyName = this.isCargoOwner 
      ? this.deal.freightForwarder.companyName 
      : this.deal.cargoOwner.companyName;
    
    this.requisitesService.getRequisitesByUserId(targetUserId).subscribe({
      next: (requisites) => {
        this.cargoOwnerRequisites = requisites;
        this.loadingRequisites = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading requisites:', err);
        if (err.status === 404) {
          this.requisitesError = 'Реквизиты не заполнены';
        } else {
          this.requisitesError = 'Ошибка при загрузке реквизитов';
        }
        this.loadingRequisites = false;
        this.cdr.markForCheck();
      }
    });
  }

  closeRequisitesModal(): void {
    this.showRequisitesModal = false;
    this.cargoOwnerRequisites = null;
    this.requisitesError = null;
    this.requisitesCompanyName = '';
  }

  openActivityModal(activity: any): void {
    this.selectedActivity = activity;
    this.showActivityModal = true;
    this.completeActivityError = null;
    this.completeActivityInlineForm.reset({
      targetMessage: ''
    });
  }

  closeActivityModal(): void {
    this.showActivityModal = false;
    this.selectedActivity = null;
    this.completeActivityError = null;
    this.completeActivityInlineForm.reset();
  }

  openAddRequestModal(): void {
    this.showAddActivityModal = true;
    this.activityError = null;
    this.activityForm.reset({
      activityTypeCode: 'REQUEST',
      actorMessage: '',
      request: true,
      targetRoleId: null
    });
  }

  openAddActivityModal(): void {
    this.openAddRequestModal();
  }

  closeAddActivityModal(): void {
    this.showAddActivityModal = false;
    this.activityError = null;
    this.activityForm.reset();
  }

  openDocumentUploadModal(docType: DealActivityTypeDto): void {
    this.selectedDocumentType = docType;
    this.showDocumentUploadModal = true;
    this.documentModalShowingUploadForm = false;
    this.documentUploadFiles = [];
    this.documentActorMessage = '';
    this.documentUploadError = null;
    const existingActivity = this.getExistingDocumentActivity(docType);
    if (existingActivity && !this.activityFilesMap.has(existingActivity.id) && !this.isLoadingFiles(existingActivity.id)) {
      this.loadActivityFiles(existingActivity.id);
    }
  }

  closeDocumentUploadModal(): void {
    this.showDocumentUploadModal = false;
    this.documentModalShowingUploadForm = false;
    this.selectedDocumentType = null;
    this.documentUploadFiles = [];
    this.documentUploadError = null;
  }

  showDocumentUploadForm(): void {
    this.documentModalShowingUploadForm = true;
  }

  showDocumentList(): void {
    this.documentModalShowingUploadForm = false;
    this.documentUploadFiles = [];
    this.documentUploadError = null;
  }

  getDocumentFilesForType(docType: DealActivityTypeDto): FileDto[] {
    const activity = this.getExistingDocumentActivity(docType);
    return activity ? this.getActivityFiles(activity.id) : [];
  }

  get isLoadingDocumentFiles(): boolean {
    if (!this.selectedDocumentType) return false;
    const activity = this.getExistingDocumentActivity(this.selectedDocumentType);
    return activity ? this.isLoadingFiles(activity.id) : false;
  }

  onDocumentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.documentUploadFiles = Array.from(input.files);
    }
  }

  removeDocumentFile(index: number): void {
    this.documentUploadFiles.splice(index, 1);
  }

  async deleteDocumentFile(file: FileDto): Promise<void> {
    if (this.deletingFileUid || !this.isCustomsBroker) return;
    if (!await this.confirmDialog.confirm(this.deleteFileText + '?')) return;
    this.deletingFileUid = file.uid;
    const activityId = file.containerId;
    this.fileService.deleteFile(file.uid).subscribe({
      next: () => {
        this.deletingFileUid = null;
        const list = this.activityFilesMap.get(activityId) || [];
        const updated = list.filter(f => f.uid !== file.uid);
        this.activityFilesMap.set(activityId, updated);
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error deleting document file:', err);
        this.deletingFileUid = null;
        this.cdr.markForCheck();
      }
    });
  }

  getExistingDocumentActivity(docType: DealActivityTypeDto): DealActivity | null {
    if (!this.deal?.activities) return null;
    return this.deal.activities.find(a => {
      const cat = (a.activityType as { category?: string })?.category;
      return cat === 'DOCUMENT' && a.activityType?.code === docType.code;
    }) ?? null;
  }

  saveDocuments(): void {
    if (!this.selectedDocumentType || !this.deal || this.documentUploadFiles.length === 0) return;

    this.creatingDocumentActivity = true;
    this.documentUploadError = null;

    const existingActivity = this.getExistingDocumentActivity(this.selectedDocumentType);
    const activityId$ = existingActivity
      ? of(existingActivity.id)
      : this.dealService.addActivity(this.deal.id, {
          activityTypeCode: this.selectedDocumentType.code,
          request: false
        }).pipe(map(a => a.id));

    activityId$.subscribe({
      next: (activityId) => {
        const uploadObservables = this.documentUploadFiles.map(file =>
          this.fileService.uploadFile(file, 'CARGO_DEAL_ACTIVITY', activityId)
        );

        forkJoin(uploadObservables).subscribe({
          next: () => {
            this.creatingDocumentActivity = false;
            this.showDocumentList();
            this.loadDeal(this.deal!.id);
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error uploading document files:', err);
            this.documentUploadError = existingActivity
              ? 'Ошибка при загрузке файлов'
              : 'Активность создана, но ошибка при загрузке файлов';
            this.creatingDocumentActivity = false;
            this.loadDeal(this.deal!.id);
            this.cdr.markForCheck();
          }
        });
      },
      error: (err) => {
        console.error('Error creating document activity:', err);
        this.documentUploadError = err.error?.message || 'Ошибка при сохранении';
        this.creatingDocumentActivity = false;
        this.cdr.markForCheck();
      }
    });
  }
  
  openCompleteActivityModal(activity: DealActivity): void {
    if (!activity.request || activity.completed) {
      return;
    }
    this.selectedActivity = activity;
    this.showCompleteActivityModal = true;
    this.completeActivityError = null;
    this.selectedFilesForComplete = [];
    this.completeActivityForm.reset({
      targetMessage: ''
    });
    // Загрузить файлы активности, если еще не загружены
    if (!this.activityFilesMap.has(activity.id) && !this.isLoadingFiles(activity.id)) {
      this.loadActivityFiles(activity.id);
    }
  }
  
  closeCompleteActivityModal(): void {
    this.showCompleteActivityModal = false;
    this.completeActivityError = null;
    this.selectedActivity = null;
    this.selectedFilesForComplete = [];
    this.completeActivityForm.reset();
  }
  
  onFilesSelectedForCompleteActivity(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.selectedFilesForComplete = Array.from(input.files);
    }
  }
  
  removeFileFromCompleteList(index: number): void {
    this.selectedFilesForComplete.splice(index, 1);
  }
  
  getSelectedFilesForComplete(): File[] {
    return this.selectedFilesForComplete;
  }

  toggleFileUpload(activityId: number): void {
    const current = this.showFileUploadMap.get(activityId) || false;
    this.showFileUploadMap.set(activityId, !current);
    // Файлы уже должны быть загружены при загрузке сделки
    // Но если по какой-то причине их нет, загружаем
    if (!current && !this.activityFilesMap.has(activityId) && !this.isLoadingFiles(activityId)) {
      this.loadActivityFiles(activityId);
    }
  }

  onFilesSelectedForActivity(event: Event, activityId: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.selectedFilesMap.set(activityId, Array.from(input.files));
    }
  }

  removeFileFromList(activityId: number, index: number): void {
    const files = this.selectedFilesMap.get(activityId) || [];
    files.splice(index, 1);
    if (files.length === 0) {
      this.selectedFilesMap.delete(activityId);
    }
  }

  loadAllActivityFiles(activityIds: number[]): void {
    if (activityIds.length === 0) return;

    // Установить флаг загрузки для всех активностей
    activityIds.forEach(id => this.loadingFilesMap.set(id, true));

    this.fileService.getFilesByContainerIds('CARGO_DEAL_ACTIVITY', activityIds).subscribe({
      next: (allFiles) => {
        // Распределить файлы по активностям
        const filesByActivity = new Map<number, FileDto[]>();
        
        allFiles.forEach(file => {
          const activityId = file.containerId;
          if (!filesByActivity.has(activityId)) {
            filesByActivity.set(activityId, []);
          }
          filesByActivity.get(activityId)!.push(file);
        });

        // Сохранить файлы для каждой активности
        activityIds.forEach(activityId => {
          this.activityFilesMap.set(activityId, filesByActivity.get(activityId) || []);
          this.loadingFilesMap.set(activityId, false);
        });

        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading activity files:', err);
        activityIds.forEach(id => this.loadingFilesMap.set(id, false));
        this.cdr.markForCheck();
      }
    });
  }

  loadActivityFiles(activityId: number): void {
    // Если файлы уже загружены, не загружаем повторно
    if (this.activityFilesMap.has(activityId)) {
      return;
    }

    this.loadingFilesMap.set(activityId, true);
    this.fileService.getFilesByContainer('CARGO_DEAL_ACTIVITY', activityId).subscribe({
      next: (files) => {
        this.activityFilesMap.set(activityId, files);
        this.loadingFilesMap.set(activityId, false);
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading files:', err);
        this.loadingFilesMap.set(activityId, false);
        this.cdr.markForCheck();
      }
    });
  }

  uploadFilesToActivity(activityId: number): void {
    const files = this.selectedFilesMap.get(activityId);
    if (!files || files.length === 0) return;

    this.uploadingFilesMap.set(activityId, true);
    
    const uploadObservables = files.map(file =>
      this.fileService.uploadFile(file, 'CARGO_DEAL_ACTIVITY', activityId)
    );

    forkJoin(uploadObservables).subscribe({
      next: (uploadedFiles) => {
        const existingFiles = this.activityFilesMap.get(activityId) || [];
        this.activityFilesMap.set(activityId, [...existingFiles, ...uploadedFiles]);
        this.selectedFilesMap.delete(activityId);
        this.uploadingFilesMap.set(activityId, false);
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error uploading files:', err);
        this.uploadingFilesMap.set(activityId, false);
        this.cdr.markForCheck();
      }
    });
  }

  getActivityFiles(activityId: number): FileDto[] {
    return this.activityFilesMap.get(activityId) || [];
  }

  isUploadingFiles(activityId: number): boolean {
    return this.uploadingFilesMap.get(activityId) || false;
  }

  isLoadingFiles(activityId: number): boolean {
    return this.loadingFilesMap.get(activityId) || false;
  }

  isFileUploadVisible(activityId: number): boolean {
    return this.showFileUploadMap.get(activityId) || false;
  }

  getSelectedFiles(activityId: number): File[] {
    return this.selectedFilesMap.get(activityId) || [];
  }

  getFileDownloadUrl(uid: string): string {
    return this.fileService.getDownloadUrl(uid);
  }

  onDownloadFile(file: FileDto, event?: Event): void {
    event?.preventDefault();
    this.fileService.downloadFile(file.uid, file.fileName);
  }

  submitActivity(): void {
    if (this.activityForm.invalid || !this.deal) return;

    this.creatingActivity = true;
    this.activityError = null;

    const formValue = this.activityForm.value;
    const request: CreateDealActivityRequest = {
      activityTypeCode: formValue.activityTypeCode,
      actorMessage: formValue.actorMessage || undefined,
      request: formValue.request || false,
      targetRoleId: formValue.request ? formValue.targetRoleId : undefined
    };

    this.dealService.addActivity(this.deal.id, request).subscribe({
      next: (createdActivity) => {
        this.creatingActivity = false;
        this.closeAddActivityModal();
        // Перезагрузить сделку и файлы
        this.loadDeal(this.deal!.id);
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error creating activity:', err);
        this.activityError = err.error?.message || 'Ошибка при создании активности';
        this.creatingActivity = false;
        this.cdr.markForCheck();
      }
    });
  }
  
  submitCompleteActivity(): void {
    if (!this.selectedActivity || !this.completeActivityForm.valid) return;

    this.completingActivity = true;
    this.completeActivityError = null;

    const formValue = this.completeActivityForm.value;
    const request: CompleteDealActivityRequest = {
      targetMessage: formValue.targetMessage || undefined
    };

    // Сначала завершаем активность
    this.dealService.completeActivity(this.selectedActivity.id, request).subscribe({
      next: (updatedActivity) => {
        // Если есть файлы для загрузки, загружаем их
        if (this.selectedFilesForComplete.length > 0) {
          const uploadObservables = this.selectedFilesForComplete.map(file =>
            this.fileService.uploadFile(file, 'CARGO_DEAL_ACTIVITY', this.selectedActivity!.id)
          );

          forkJoin(uploadObservables).subscribe({
            next: (uploadedFiles) => {
              this.completingActivity = false;
              this.closeCompleteActivityModal();
              // Перезагрузить сделку
              this.loadDeal(this.deal!.id);
              this.cdr.markForCheck();
            },
            error: (err) => {
              console.error('Error uploading files:', err);
              // Активность уже завершена, но файлы не загружены
              // Показываем предупреждение, но закрываем модальное окно
              this.completingActivity = false;
              this.completeActivityError = 'Активность завершена, но произошла ошибка при загрузке файлов';
              // Перезагрузить сделку
              this.loadDeal(this.deal!.id);
              this.cdr.markForCheck();
            }
          });
        } else {
          // Нет файлов для загрузки, просто закрываем
          this.completingActivity = false;
          this.closeCompleteActivityModal();
          // Перезагрузить сделку
          this.loadDeal(this.deal!.id);
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        console.error('Error completing activity:', err);
        this.completeActivityError = err.error?.message || 'Ошибка при завершении активности';
        this.completingActivity = false;
        this.cdr.markForCheck();
      }
    });
  }
  
  submitCompleteActivityInline(): void {
    if (!this.selectedActivity || !this.completeActivityInlineForm.valid) return;

    this.completingActivity = true;
    this.completeActivityError = null;

    const formValue = this.completeActivityInlineForm.value;
    const request: CompleteDealActivityRequest = {
      targetMessage: formValue.targetMessage || undefined
    };

    // Завершаем активность
    this.dealService.completeActivity(this.selectedActivity.id, request).subscribe({
      next: (updatedActivity) => {
        this.completingActivity = false;
        this.completeActivityError = null;
        
        // Обновляем выбранную активность с новыми данными
        this.selectedActivity = updatedActivity;
        
        // Обновляем активность в списке активностей сделки
        if (this.deal && this.deal.activities) {
          const index = this.deal.activities.findIndex(a => a.id === updatedActivity.id);
          if (index !== -1) {
            // Обновляем все поля активности
            Object.assign(this.deal.activities[index], updatedActivity);
          }
        }
        
        // Сбрасываем форму
        this.completeActivityInlineForm.reset({
          targetMessage: ''
        });
        
        // Принудительно обновляем представление
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error completing activity:', err);
        this.completeActivityError = err.error?.message || 'Ошибка при завершении активности';
        this.completingActivity = false;
        this.cdr.markForCheck();
      }
    });
  }

  openReport(): void {
    if (!this.deal) return;
    const base = this.isCargoOwner ? '/cargo-owner/deal' : this.isCustomsBroker ? '/customs-broker/deal' : '/freight-forwarder/deal';
    this.router.navigate([base, this.deal.id, 'report']);
  }


  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const lang = this.languageService.getCurrentLanguageValue();
    return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      'CREATED': 'Создана',
      'IN_PROGRESS': 'В процессе',
      'COMPLETED': 'Завершена',
      'CANCELLED': 'Отменена'
    };
    return statusMap[status] || status;
  }

  getServiceName(service: any): string {
    const lang = this.languageService.getCurrentLanguageValue();
    return lang === 'en' ? service.nameEn : service.nameRu;
  }

  getCountryName(country: any): string {
    if (!country) return '-';
    const lang = this.languageService.getCurrentLanguageValue();
    return lang === 'en' ? country.nameEn : country.nameRu;
  }

  getActivityCategory(activity: DealActivity): string | null {
    const cat = (activity?.activityType as { category?: string })?.category;
    return cat || null;
  }

  getActivityCategoryLabel(activity: DealActivity): string {
    const cat = this.getActivityCategory(activity);
    if (cat === 'DOCUMENT') return this.documentLabel;
    if (cat === 'STATUS') return this.statusLabel;
    if (cat === 'REQUEST') return this.requestLabel;
    return '';
  }

  getActivityText(activity: DealActivity): string {
    if (!activity || !activity.activityType) {
      return 'Неизвестный тип активности';
    }
    const category = (activity.activityType as { category?: string }).category;
    const lang = this.languageService.getCurrentLanguageValue();
    const activityName = lang === 'en' ? activity.activityType.nameEn : activity.activityType.nameRu;
    if (category === 'REQUEST' && activity.actorMessage) {
      return activity.actorMessage;
    }
    if (activity.actorMessage) {
      // Сервер часто повторяет название в начале сообщения («Сделка создана. …») — не дублируем
      const msg = activity.actorMessage.trim();
      return msg.toLowerCase().startsWith((activityName || '').toLowerCase()) ? msg : `${activityName}: ${msg}`;
    }
    return activityName;
  }

  /** Этап для карточки «Статус перевозки»: название крупно, пояснение отдельно и без повтора названия. */
  stageTitle(activity: DealActivity): string {
    const lang = this.languageService.getCurrentLanguageValue();
    return (lang === 'en' ? activity.activityType?.nameEn : activity.activityType?.nameRu) || this.getActivityText(activity);
  }

  stageNote(activity: DealActivity): string {
    const title = this.stageTitle(activity);
    let msg = (activity.actorMessage || '').trim();
    if (msg.toLowerCase().startsWith(title.toLowerCase())) msg = msg.slice(title.length).replace(/^[\s.:,—-]+/, '');
    return msg;
  }

  getRequestDisplayText(req: DealActivity): string {
    return req.actorMessage || this.getActivityTypeName(req);
  }

  // ─── Пока открыто любое окно — страница под ним не прокручивается ───
  private modalScrollLocked = false;

  private get anyModalOpen(): boolean {
    return this.showRequestModal || this.showBidModal || this.showRequisitesModal || this.showActivityModal
      || this.showAddActivityModal || this.showDocumentUploadModal || this.showAddStatusModal || this.showCompleteActivityModal
      || this.stageNoteOpen || this.showPaymentModal || this.showInvoiceModal || !!this.revokingInvoice || !!this.revokingPayment || !!this.preview;
  }

  ngDoCheck(): void {
    const open = this.anyModalOpen;
    if (open === this.modalScrollLocked) return;
    this.modalScrollLocked = open;
    if (open) this.lockScroll();
    else if (!this.chatOpen) this.unlockScroll();
  }

  // ─── «Скопировать» реквизиты — простым текстом, как на странице запроса ───
  requisitesCopied = false;

  async copyRequisites(): Promise<void> {
    const r: any = this.cargoOwnerRequisites;
    if (!r) return;
    const lines = [
      this.requisitesCompanyName,
      r.bin && 'ИИН/БИН: ' + r.bin,
      r.legalAddress && 'Юридический адрес: ' + r.legalAddress,
      r.bankName && 'Банк: ' + r.bankName,
      r.bik && 'БИК: ' + r.bik,
      ...(r.bankAccounts ?? []).map((a: any) => 'Счёт ' + (a.currency || '') + ': ' + a.account),
      r.contractSignerPosition && 'Должность: ' + r.contractSignerPosition,
      r.contractSignerName && 'ФИО: ' + r.contractSignerName,
    ].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(lines);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = lines;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { return; } finally { ta.remove(); }
    }
    this.requisitesCopied = true;
    setTimeout(() => { this.requisitesCopied = false; this.cdr.markForCheck(); }, 2000);
    this.cdr.markForCheck();
  }

  /** Полный комментарий к этапу — в маленьком окне по «Подробнее». */
  stageNoteOpen = false;

  /** Статус обновлён сегодня (по времени пользователя) — для напоминания экспедитору. */
  get stageUpdatedToday(): boolean {
    const last = (this.deal?.activities ?? [])
      .filter(a => a.activityType?.category === 'STATUS')
      .map(a => new Date(a.createdAt))
      .sort((a, b) => b.getTime() - a.getTime())[0];
    return !!last && last.toDateString() === new Date().toDateString();
  }

  // ─── Оплата за перевозку: счета и оплаты в валюте счёта ───

  get payCurrency(): string {
    return this.deal?.bid?.currencyCode || '';
  }

  get payTotal(): number {
    return this.deal?.bid?.price || 0;
  }

  get pay() {
    return paymentSummary(this.deal?.activities);
  }

  readonly invoiceCurrencies = INVOICE_CURRENCIES;

  /** Доля оплаченного от выставленного, % (для полоски) — в среднем по валютам счетов. */
  get payPercent(): number {
    const t = this.pay.totals.filter(x => x.invoiced > 0);
    if (!t.length) return 0;
    return Math.round((t.reduce((s, x) => s + Math.min(1, x.paid / x.invoiced), 0) / t.length) * 100);
  }

  get payState(): 'noInvoice' | 'none' | 'partial' | 'paid' | 'over' {
    const p = this.pay;
    if (!p.activeInvoices.length) return 'noInvoice';
    if (p.totals.every(x => x.paid <= 0)) return 'none';
    if (p.hasDebt) return 'partial';
    return p.hasOverpay ? 'over' : 'paid';
  }

  fmtKzt(v: number): string {
    return this.fmtCur(v, 'KZT');
  }

  /** Сумма с валютой: тенге — знаком ₸, остальные — кодом. */
  fmtCur(v: number, currency: string): string {
    const n = new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 2 }).format(v || 0);
    return currency === 'KZT' ? n + ' ₸' : n + ' ' + currency;
  }

  /** Итог по всем валютам одной строкой: «1 130 300 ₸ · 500 USD». */
  fmtTotals(field: 'invoiced' | 'paid' | 'pending' | 'debt' | 'overpaid'): string {
    const parts = this.pay.totals.filter(t => t[field] > 0).map(t => this.fmtCur(t[field], t.currency));
    return parts.length ? parts.join(' · ') : this.fmtCur(0, this.pay.totals[0]?.currency || 'KZT');
  }

  private todayIso(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }

  private isoToRu(iso: string): string {
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  }

  // Окно «Выставить счёт»
  showInvoiceModal = false;
  invoiceNumber = '';
  invoiceDate = '';
  /** Сумма и валюта счёта — как в счёте экспедитора. */
  invoiceAmount: number | null = null;
  invoiceCurrency = 'KZT';
  invoiceFile: File | null = null;
  /** Изменяемый счёт; null — выставляется новый. */
  editingInvoice: DealInvoice | null = null;

  openInvoiceModal(): void {
    const active = this.pay.activeInvoices;
    this.editingInvoice = null;
    this.invoiceNumber = String(this.pay.invoices.length + 1);
    this.invoiceDate = this.todayIso();
    this.invoiceAmount = null;
    this.invoiceCurrency = active[active.length - 1]?.currency || 'KZT';
    this.invoiceFile = null;
    this.paymentError = null;
    this.showInvoiceModal = true;
  }

  /** Текущее время — раз в 20 секунд, чтобы «Изменить» пропадало через 10 минут после отправки. */
  now = Date.now();
  private nowTimer = setInterval(() => {
    this.now = Date.now();
    this.cdr.markForCheck();
  }, 20000);

  /** Счёт меняет экспедитор — в течение 10 минут после выставления. */
  canEditInvoice(i: DealInvoice): boolean {
    return this.isFreightForwarder && !i.revoked && isEditable(i.createdAt, this.now);
  }

  /** Изменить выставленный счёт — то же окно, заполненное данными счёта. */
  openInvoiceEdit(i: DealInvoice): void {
    this.editingInvoice = i;
    this.invoiceNumber = i.number;
    this.invoiceDate = this.ruToIso(i.date);
    this.invoiceAmount = i.amount;
    this.invoiceCurrency = i.currency;
    this.invoiceFile = null;
    this.paymentError = null;
    this.showInvoiceModal = true;
  }

  private ruToIso(d: string): string {
    const m = d.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
  }

  closeInvoiceModal(): void {
    this.showInvoiceModal = false;
  }

  onInvoiceFile(event: Event): void {
    this.invoiceFile = (event.target as HTMLInputElement).files?.[0] ?? null;
  }

  saveInvoice(): void {
    if (!this.deal || !this.invoiceNumber.trim() || !this.invoiceDate || !this.invoiceAmount || this.invoiceAmount <= 0) {
      this.paymentError = this.t('ffPay.invoiceFillErrorKzt', 'Укажите номер, дату и сумму');
      return;
    }
    const ed = this.editingInvoice;
    if (ed && !isEditable(ed.createdAt)) {
      this.paymentError = this.t('ffPay.editExpired', 'Прошло 10 минут — изменить уже нельзя. Отзовите и создайте заново.');
      return;
    }
    const message = ed
      ? formatInvoiceEditMessage(ed.number, this.invoiceNumber, this.isoToRu(this.invoiceDate), this.invoiceAmount, this.invoiceCurrency)
      : formatInvoiceMessage(this.invoiceNumber, this.isoToRu(this.invoiceDate), this.invoiceAmount, this.invoiceCurrency);
    if (ed && message === formatInvoiceEditMessage(ed.number, ed.number, ed.date, ed.amount, ed.currency) && !this.invoiceFile) {
      this.showInvoiceModal = false;
      return;
    }
    // Файл счёта кладём в «Документы → Другие документы», с номером счёта в имени
    const file = this.invoiceFile
      ? new File([this.invoiceFile], `Счёт № ${this.invoiceNumber.trim()} — ${this.invoiceFile.name}`, { type: this.invoiceFile.type })
      : null;
    this.saveMoneyActivity(INVOICE_ACTIVITY_CODE, message, file, () => (this.showInvoiceModal = false), OTHER_DOCS_CODE);
  }

  // Отзыв счёта: счёт остаётся в списке зачёркнутым и в итоги не входит
  revokingInvoice: DealInvoice | null = null;
  revokeReason = '';

  openInvoiceRevoke(i: DealInvoice): void {
    this.revokingInvoice = i;
    this.revokeReason = '';
    this.paymentError = null;
  }

  revokingPayment: DealPayment | null = null;

  openPaymentRevoke(x: DealPayment): void {
    this.revokingPayment = x;
    this.revokeReason = '';
    this.paymentError = null;
  }

  closeRevoke(): void {
    this.revokingInvoice = null;
    this.revokingPayment = null;
  }

  saveRevoke(): void {
    const i = this.revokingInvoice;
    if (i) {
      this.saveMoneyActivity(INVOICE_ACTIVITY_CODE, formatInvoiceRevokeMessage(i.number, this.revokeReason), null, () => this.closeRevoke());
      return;
    }
    const x = this.revokingPayment;
    if (!x) return;
    if (this.revokeNeedsConsent(x)) {
      // Подтверждённую оплату — только с согласия экспедитора: отправляем ему запрос
      const forwarderRoleId = this.roles.find(r => r.code === 'ROLE_FREIGHT_FORWARDER')?.id;
      this.saveMoneyActivity(PAYMENT_ACTIVITY_CODE, formatPaymentRevokeRequestMessage(x, this.revokeReason), null, () => this.closeRevoke(),
        undefined, { request: true, targetRoleId: forwarderRoleId });
      return;
    }
    this.saveMoneyActivity(PAYMENT_ACTIVITY_CODE, formatPaymentRevokeMessage(x, this.revokeReason), null, () => this.closeRevoke());
  }

  // Окно «Подтвердить оплату» / «Отправить платёжку» — в валюте счёта
  showPaymentModal = false;
  paymentAmount: number | null = null;
  paymentCurrency = 'KZT';
  paymentNote = '';
  /** Номер и дата платёжного поручения — необязательно; попадают в комментарий оплаты («п/п № 123 от 18.10.2026»). */
  paymentOrderNo = '';
  paymentOrderDate = '';
  paymentFile: File | null = null;
  /** Изменяемая оплата; null — новая. */
  editingPayment: DealPayment | null = null;
  paymentSaving = false;
  paymentError: string | null = null;

  /** Валюты, в которых выставлены счета, — в них и платят. */
  get paymentCurrencies(): string[] {
    const list = this.pay.totals.filter(t => t.invoiced > 0).map(t => t.currency);
    // При изменении оплаты — и её валюта, даже если счёта в ней уже нет
    const cur = this.editingPayment?.currency;
    if (cur && !list.includes(cur)) list.push(cur);
    return list.length ? list : ['KZT'];
  }

  /** Сумма в окне оплаты больше задолженности — будет переплата (показываем предупреждение). */
  get paymentExcess(): number {
    if (this.editingPayment || !this.paymentAmount) return 0;
    const t = this.pay.totals.find(x => x.currency === this.paymentCurrency);
    // Неподтверждённые платёжки тоже уменьшают остаток
    const left = Math.max(0, (t?.debt ?? 0) - (t?.pending ?? 0));
    return Math.max(0, Math.round((this.paymentAmount - left) * 100) / 100);
  }

  /** Задолженность в выбранной валюте оплаты. */
  get paymentDebt(): number {
    return this.pay.totals.find(t => t.currency === this.paymentCurrency)?.debt ?? 0;
  }

  onPaymentCurrency(): void {
    this.paymentAmount = this.paymentDebt || null;
  }

  openPaymentModal(): void {
    this.editingPayment = null;
    const withDebt = this.pay.totals.find(t => t.debt > 0);
    this.paymentCurrency = withDebt?.currency || this.paymentCurrencies[0];
    this.paymentAmount = this.paymentDebt || null;
    this.paymentNote = '';
    this.paymentOrderNo = '';
    this.paymentOrderDate = '';
    this.paymentFile = null;
    this.paymentError = null;
    this.showPaymentModal = true;
  }

  /** Оплату меняет тот, кто её внёс, — в течение 10 минут; платёжку клиента — пока её не подтвердили. */
  canEditPayment(x: DealPayment): boolean {
    if (x.revoked || !isEditable(x.createdAt, this.now)) return false;
    return x.fromOwner ? this.isCargoOwner && x.pending : this.isFreightForwarder;
  }

  /** Отзывает оплату только клиент: неподтверждённую — сразу, подтверждённую — запросом экспедитору. */
  canRevokePayment(x: DealPayment): boolean {
    return this.isCargoOwner && !x.revoked && (x.pending || x.revokeRequest?.state !== 'pending');
  }

  /** Платёжка уже подтверждена — отозвать можно только с согласия экспедитора. */
  revokeNeedsConsent(x: DealPayment): boolean {
    return this.isCargoOwner && !x.pending;
  }

  openPaymentEdit(x: DealPayment): void {
    this.editingPayment = x;
    this.paymentCurrency = x.currency;
    this.paymentAmount = x.amount;
    this.paymentOrderDate = this.ruToIso(x.date);
    // Комментарий оплаты: «п/п № 123, комментарий»
    const m = x.note.match(/^п\/п № ([^,]+?)(?:,\s*([\s\S]*))?$/);
    this.paymentOrderNo = m ? m[1].trim() : '';
    this.paymentNote = m ? (m[2] || '').trim() : x.note;
    this.paymentFile = null;
    this.paymentError = null;
    this.showPaymentModal = true;
  }

  closePaymentModal(): void {
    this.showPaymentModal = false;
  }

  onPaymentFile(event: Event): void {
    this.paymentFile = (event.target as HTMLInputElement).files?.[0] ?? null;
  }

  savePayment(): void {
    if (!this.deal || !this.paymentAmount || this.paymentAmount <= 0) {
      this.paymentError = this.t('ffPay.fillErrorAmount', 'Укажите сумму оплаты');
      return;
    }
    const ed = this.editingPayment;
    if (ed && !isEditable(ed.createdAt)) {
      this.paymentError = this.t('ffPay.editExpired', 'Прошло 10 минут — изменить уже нельзя. Отзовите и создайте заново.');
      return;
    }
    // Грузовладелец отправляет платёжку экспедитору: без файла платёжного поручения нельзя (при изменении — файл прежний)
    if (this.isCargoOwner && !ed && !this.paymentFile) {
      this.paymentError = this.t('ffPay.fileRequired', 'Прикрепите платёжное поручение');
      return;
    }
    // Дата оплаты — дата платёжки, а если её не указали — сегодня; номер платёжки — в комментарий
    const date = this.isoToRu(this.paymentOrderDate || this.todayIso());
    const note = [this.paymentOrderNo.trim() && 'п/п № ' + this.paymentOrderNo.trim(), this.paymentNote.trim()].filter(Boolean).join(', ');
    if (ed) {
      const msg = formatPaymentEditMessage(ed, this.paymentAmount, this.paymentCurrency, date, note);
      const file = this.paymentFile;
      this.saveMoneyActivity(PAYMENT_ACTIVITY_CODE, msg, file, () => (this.showPaymentModal = false), OTHER_DOCS_CODE, undefined,
        () => this.paymentFilePrefix(ed.activityId) + file!.name);
      return;
    }
    const message = formatPaymentMessage(this.paymentAmount, this.paymentCurrency, date, note);
    const forwarderRoleId = this.roles.find(r => r.code === 'ROLE_FREIGHT_FORWARDER')?.id;
    const asRequest = this.isCargoOwner ? { request: true, targetRoleId: forwarderRoleId } : undefined;
    const file = this.paymentFile;
    this.saveMoneyActivity(PAYMENT_ACTIVITY_CODE, message, file, () => (this.showPaymentModal = false), OTHER_DOCS_CODE, asRequest,
      id => this.paymentFilePrefix(id) + file!.name);
  }

  /** Экспедитор подтверждает платёжку клиента — после этого сумма идёт в «Оплачено». */
  confirmingPaymentId: number | null = null;

  confirmPayment(p: DealPayment): void {
    if (!this.deal || this.confirmingPaymentId) return;
    this.confirmingPaymentId = p.activityId;
    this.paymentError = null;
    this.dealService.completeActivity(p.activityId, { targetMessage: this.t('ffPay.confirmedMsg', 'Оплата получена') }).subscribe({
      next: () => {
        this.confirmingPaymentId = null;
        this.loadDeal(this.deal!.id);
        this.cdr.markForCheck();
      },
      error: err => {
        this.confirmingPaymentId = null;
        this.paymentError = err.error?.message || this.t('ffPay.confirmError', 'Не удалось подтвердить оплату');
        this.cdr.markForCheck();
      }
    });
  }

  /** Экспедитор отвечает на запрос клиента отозвать подтверждённую оплату. */
  answerRevokeRequest(x: DealPayment, approve: boolean): void {
    const rq = x.revokeRequest;
    if (!this.deal || !rq || this.confirmingPaymentId) return;
    this.confirmingPaymentId = x.activityId;
    this.paymentError = null;
    this.dealService.completeActivity(rq.activityId, { targetMessage: approve ? REVOKE_APPROVED : REVOKE_DECLINED }).subscribe({
      next: () => {
        this.confirmingPaymentId = null;
        this.loadDeal(this.deal!.id);
        this.cdr.markForCheck();
      },
      error: err => {
        this.confirmingPaymentId = null;
        this.paymentError = err.error?.message || this.t('ffPay.saveError', 'Не удалось сохранить');
        this.cdr.markForCheck();
      }
    });
  }

  /** Файл счёта: в самом событии (старые счета) или в «Других документах» с номером счёта в имени. */
  invoiceFileOf(i: DealInvoice): FileDto | null {
    const own = this.getActivityFiles(i.activityId);
    if (own.length) return own[0];
    const other = (this.deal?.activities ?? []).find(a => a.activityType?.code === OTHER_DOCS_CODE);
    if (!other) return null;
    // Самый свежий файл: сначала по текущему номеру, затем по прежним
    const files = [...this.getActivityFiles(other.id)].reverse();
    for (const n of [...i.numbers].reverse()) {
      const f = files.find(x => (x.fileName || '').startsWith('Счёт № ' + n + ' — '));
      if (f) return f;
    }
    return null;
  }

  /** Имя файла платёжки в «Других документах»: «Платёжка к оплате #389 — файл.pdf». */
  private paymentFilePrefix(paymentId: number): string {
    return `Платёжка к оплате #${paymentId} — `;
  }

  /** Файл платёжного поручения к оплате. */
  paymentFileOf(p: DealPayment): FileDto | null {
    // Новые платёжки лежат в «Других документах» с номером оплаты в имени — берём самую свежую
    const other = (this.deal?.activities ?? []).find(a => a.activityType?.code === OTHER_DOCS_CODE);
    const prefix = this.paymentFilePrefix(p.activityId);
    const inOther = other ? [...this.getActivityFiles(other.id)].reverse().find(f => (f.fileName || '').startsWith(prefix)) : null;
    if (inOther) return inOther;
    // Старые — в самом событии оплаты или его изменений
    for (const id of [...p.fileActivityIds].reverse()) {
      const f = this.getActivityFiles(id)[0];
      if (f) return f;
    }
    return null;
  }

  /**
   * Сохраняет счёт или оплату событием сделки (+ файл), затем перезагружает сделку.
   * fileDocCode — файл кладётся не в само событие, а в раздел «Документы» с этим кодом.
   */
  private saveMoneyActivity(
    code: string, message: string, file: File | null, close: () => void, fileDocCode?: string,
    asRequest?: { request: boolean; targetRoleId?: number }, fileName?: (activityId: number) => string
  ): void {
    if (!this.deal) return;
    const dealId = this.deal.id;
    this.paymentSaving = true;
    this.paymentError = null;
    this.dealService.addActivity(dealId, { activityTypeCode: code, actorMessage: message, request: false, ...(asRequest ?? {}) }).subscribe({
      next: activity => {
        const done = () => {
          this.paymentSaving = false;
          close();
          this.loadDeal(dealId);
          this.cdr.markForCheck();
        };
        if (!file) {
          done();
          return;
        }
        // Документ этого типа уже есть — добавляем файл к нему, нет — создаём
        const existing = fileDocCode ? this.deal?.activities?.find(a => a.activityType?.code === fileDocCode) : null;
        const target$ = !fileDocCode
          ? of(activity.id)
          : existing
            ? of(existing.id)
            : this.dealService.addActivity(dealId, { activityTypeCode: fileDocCode, request: false }).pipe(map(a => a.id));
        const named = fileName ? new File([file], fileName(activity.id), { type: file.type }) : file;
        // Не вышло положить в «Документы» — кладём файл в само событие, чтобы он не потерялся
        const fallback = () => this.fileService.uploadFile(named, 'CARGO_DEAL_ACTIVITY', activity.id).subscribe({ next: done, error: done });
        target$.subscribe({
          next: targetId => this.fileService.uploadFile(named, 'CARGO_DEAL_ACTIVITY', targetId).subscribe({
            next: done,
            error: () => (targetId === activity.id ? done() : fallback()),
          }),
          error: fallback,
        });
      },
      error: err => {
        this.paymentError = err.error?.message || this.t('ffPay.saveError', 'Не удалось сохранить');
        this.paymentSaving = false;
        this.cdr.markForCheck();
      }
    });
  }

  /** «Активности» свёрнуты по умолчанию — видно количество и последнее событие. */
  activitiesOpen = false;

  /** События сделки — новые сверху. */
  get sortedActivities(): DealActivity[] {
    return [...(this.deal?.activities ?? [])].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  /** Документы сделки, которые показываем — только основные, в этом порядке. */
  private static readonly DOCUMENT_CODES = [
    'DOC_FREIGHT_AGREEMENT',
    'DOC_FREIGHT_REQUEST',
    'DOC_SHIPPING_CMR',
    'DOC_COMMERCIAL_INVOICE',
    'DOC_PACKING_LIST',
    'DOC_OTHER',
  ];

  get documentTypes(): DealActivityTypeDto[] {
    const order = ViewDealComponent.DOCUMENT_CODES;
    if (this.documentsOnly) {
      // Все папки сервера: сначала основные в привычном порядке, «Другие документы» — последними
      const rank = (code: string) => code === 'DOC_OTHER' ? 999 : order.includes(code) ? order.indexOf(code) : 100;
      return this.activityTypes.filter(t => t.category === 'DOCUMENT').sort((a, b) => rank(a.code) - rank(b.code));
    }
    return this.activityTypes
      .filter(t => t.category === 'DOCUMENT' && order.includes(t.code))
      .sort((a, b) => order.indexOf(a.code) - order.indexOf(b.code));
  }

  /** Этапы перевозки в «Изменить статус» — по ходу перевозки (коды с сервера). */
  private static readonly STATUS_FLOW = [
    'PICKUP_DATE_SET',               // Установлена дата забора
    'CARGO_PICKUP_DONE',             // Забор груза произведен
    'CARGO_AT_FORWARDER_WAREHOUSE',  // Груз на складе экспедитора
    'CONSOLIDATION_IN_PROGRESS',     // Идет консолидация
    'EXPORT_DECLARATION_PROCESSING', // Оформление экспортной декларации
    'IN_TRANSIT',                    // Груз в пути
    'TRANSIT_CLEARANCE',             // Транзитное оформление (появится, когда добавят на сервере)
    'DEPARTED_FROM_BORDER',          // Выезд с границы
    'CARGO_AT_CUSTOMS_WAREHOUSE',    // Груз на СВХ
    'DELIVERED',                     // Груз доставлен
    'CUSTOMS_CLEARANCE_IN_PROGRESS', // Идет таможенная очистка
    'GOODS_RELEASED',                // Товар выпущен
    'ARRIVED_AT_DESTINATION',        // Прибыл в пункт назначения
    'AT_RECEIVER_WAREHOUSE',         // На складе получателя
  ];

  /** Отклонения — отдельной группой в конце списка. */
  private static readonly STATUS_ISSUES = ['DELAY_REPORTED', 'ISSUE_REPORTED'];

  private statusTypesByCodes(codes: string[]): DealActivityTypeDto[] {
    return codes
      .map(code => this.activityTypes.find(t => t.category === 'STATUS' && t.code === code))
      .filter((t): t is DealActivityTypeDto => !!t);
  }

  /** «Сделка создана» ставится сама, «Груз принят» убран — в список не попадают. */
  get statusActivityTypes(): DealActivityTypeDto[] {
    return this.statusTypesByCodes(ViewDealComponent.STATUS_FLOW);
  }

  get statusIssueTypes(): DealActivityTypeDto[] {
    return this.statusTypesByCodes(ViewDealComponent.STATUS_ISSUES);
  }

  get requestActivities(): DealActivity[] {
    if (!this.deal?.activities) return [];
    return this.deal.activities.filter(a => (a.activityType as { category?: string })?.category === 'REQUEST');
  }

  get lastStatusActivity(): DealActivity | null {
    if (!this.deal?.activities) return null;
    const statusActivities = this.deal.activities
      .filter(a => (a.activityType as { category?: string })?.category === 'STATUS')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return statusActivities[0] ?? null;
  }

  /** Сколько файлов загружено в документ этого типа. */
  documentCount(docType: DealActivityTypeDto): number {
    return (this.deal?.activities ?? [])
      .filter(a => (a.activityType as { category?: string })?.category === 'DOCUMENT' && a.activityType?.code === docType.code)
      .reduce((s, a) => s + this.getActivityFiles(a.id).length, 0);
  }

  /** «1 файл», «2 файла», «5 файлов». */
  filesCountText(n: number): string {
    if (this.languageService.getCurrentLanguageValue() !== 'ru') return `${n} ${(n === 1 && this.languageService.getCurrentLanguageValue() === 'en' ? 'file' : this.t('ffDocs.filesShort', 'files'))}`;
    const m10 = n % 10, m100 = n % 100;
    return `${n} ${m10 === 1 && m100 !== 11 ? 'файл' : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? 'файла' : 'файлов'}`;
  }

  // ─── Просмотр файла без скачивания ───

  preview: { file: FileDto; kind: 'pdf' | 'image' | 'text' | 'other'; url: SafeResourceUrl | null; loading: boolean; error: boolean } | null = null;
  private previewObjectUrl: string | null = null;

  private previewKind(f: FileDto): 'pdf' | 'image' | 'text' | 'other' {
    const type = (f.contentType || '').toLowerCase();
    const ext = (f.fileName || '').toLowerCase().split('.').pop() || '';
    if (type.includes('pdf') || ext === 'pdf') return 'pdf';
    if (type.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
    if (type.startsWith('text/') || ['txt', 'csv'].includes(ext)) return 'text';
    return 'other';
  }

  openPreview(file: FileDto, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closePreview();
    const kind = this.previewKind(file);
    this.preview = { file, kind, url: null, loading: kind !== 'other', error: false };
    if (kind === 'other') return;
    this.fileService.getFileBlob(file.uid).subscribe({
      next: blob => {
        if (this.preview?.file !== file) return;
        // Сервер может отдать «octet-stream» — подсказываем браузеру тип, чтобы он показал файл, а не скачал
        const type = kind === 'pdf' ? 'application/pdf' : kind === 'text' ? 'text/plain;charset=utf-8' : (blob.type || file.contentType || 'image/*');
        this.previewObjectUrl = URL.createObjectURL(new Blob([blob], { type }));
        this.preview = { ...this.preview, url: this.sanitizer.bypassSecurityTrustResourceUrl(this.previewObjectUrl), loading: false };
        this.cdr.markForCheck();
      },
      error: () => {
        if (this.preview?.file === file) this.preview = { ...this.preview, loading: false, error: true };
        this.cdr.markForCheck();
      }
    });
  }

  closePreview(): void {
    if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
    this.previewObjectUrl = null;
    this.preview = null;
  }

  hasDocumentsForType(docType: DealActivityTypeDto): boolean {
    if (!this.deal?.activities) return false;
    return this.deal.activities.some(a => {
      const cat = (a.activityType as { category?: string })?.category;
      if (cat !== 'DOCUMENT' || a.activityType?.code !== docType.code) return false;
      const files = this.getActivityFiles(a.id);
      return files.length > 0;
    });
  }

  openAddStatusModal(): void {
    this.showAddStatusModal = true;
    this.statusError = null;
    this.statusForm.reset({
      activityTypeCode: '',
      actorMessage: ''
    });
  }

  closeAddStatusModal(): void {
    this.showAddStatusModal = false;
    this.statusError = null;
    this.statusForm.reset();
  }

  submitStatusActivity(): void {
    if (this.statusForm.invalid || !this.deal) return;

    this.creatingStatus = true;
    this.statusError = null;

    const formValue = this.statusForm.value;
    const request: CreateDealActivityRequest = {
      activityTypeCode: formValue.activityTypeCode,
      actorMessage: formValue.actorMessage?.trim() || undefined,
      request: false
    };

    this.dealService.addActivity(this.deal.id, request).subscribe({
      next: () => {
        this.creatingStatus = false;
        this.closeAddStatusModal();
        this.loadDeal(this.deal!.id);
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error creating status activity:', err);
        this.statusError = err.error?.message || 'Ошибка при обновлении статуса';
        this.creatingStatus = false;
        this.cdr.markForCheck();
      }
    });
  }

  getTypeName(type: DealActivityTypeDto | { nameRu?: string; nameEn?: string; name?: string }): string {
    if (!type) return '';
    if ('name' in type && type.name) return type.name;
    const lang = this.languageService.getCurrentLanguageValue();
    return lang === 'en' ? (type as DealActivityTypeDto).nameEn : (type as DealActivityTypeDto).nameRu;
  }
  
  getActivityTypeName(activity: DealActivity): string {
    if (!activity || !activity.activityType) {
      return 'Неизвестный тип';
    }
    const lang = this.languageService.getCurrentLanguageValue();
    return lang === 'en' ? activity.activityType.nameEn : activity.activityType.nameRu;
  }
  
  canCompleteActivity(activity: DealActivity): boolean {
    if (!activity || !activity.request || activity.completed) {
      return false;
    }
    
    // Проверяем, что текущий пользователь - целевая роль
    const user = this.authService.getUser();
    if (!user || !user.role) {
      return false;
    }
    
    // Если целевая роль не указана, нельзя завершить
    if (!activity.targetRole) {
      return false;
    }
    
    // Проверяем, что роль пользователя совпадает с целевой ролью
    return user.role.code === activity.targetRole.code;
  }
  
  canUploadFilesToActivity(activity: DealActivity): boolean {
    if (!activity) return false;
    const category = (activity.activityType as { category?: string })?.category;
    return (this.isFreightForwarder || this.isCargoOwner) && category === 'DOCUMENT';
  }
  
  getTargetRoleName(role: RoleDto): string {
    const lang = this.languageService.getCurrentLanguageValue();
    return lang === 'en' ? role.name : role.name;
  }

  /** Администратор или менеджер назначил (или снял) оператора сделки. */
  onOperatorAssigned(change: AssigneeChange): void {
    if (this.deal) {
      this.deal = { ...this.deal, operatorId: change.id ?? undefined, operatorName: change.name ?? undefined };
    }
  }

  /**
   * Вести сделку (этапы, оплата, документы, чат) могут администратор и оператор; менеджер после сделки только
   * назначает оператора. Пока данные о компании не загружены, не ограничиваем: решает сервер.
   */
  get canWorkDeal(): boolean {
    return !this.companyContext.company || this.companyContext.hasPermission('WORK_DEALS');
  }
}
